import { Github, Linkedin, Twitter } from "lucide-react";

import { Logo, Wordmark } from "./Logo";
import { SmartLink } from "./SmartLink";
import { site } from "../data/site";
import { PILLARS } from "../data/capabilities";
import { PRODUCTS } from "../data/products";

export function Footer() {
  return (
    <footer className="relative border-t border-white/5 bg-ink-950/80">
      <div className="container py-14">
        <div className="grid gap-10 md:grid-cols-[1.4fr_1fr_1fr_1fr]">
          <div className="space-y-4">
            <SmartLink
              href="/"
              className="flex items-center gap-2 text-base font-semibold"
              aria-label="astalabista — home"
            >
              <Logo className="h-7 w-7" id="asta-mark-footer" />
              <Wordmark />
            </SmartLink>
            <p className="max-w-xs text-sm leading-relaxed text-white/55">
              {site.descriptor}
            </p>
            <div className="flex items-center gap-2 pt-1">
              <Social
                href={site.social.linkedin}
                label="astalabista on LinkedIn"
                icon={Linkedin}
              />
              <Social
                href={site.social.github}
                label="astalabista on GitHub"
                icon={Github}
              />
              <Social href={site.social.x} label="astalabista on X" icon={Twitter} />
            </div>
          </div>

          <FooterCol
            title="What we do"
            links={[
              ...PILLARS.map((p) => ({ href: p.href, label: p.title })),
              { href: "/what-we-do", label: "How it fits together" },
            ]}
          />

          <FooterCol
            title="Products"
            links={[
              ...PRODUCTS.map((p) => ({ href: p.href, label: p.name })),
              { href: "/products", label: "All products" },
            ]}
          />

          <FooterCol
            title="Company"
            links={[
              { href: "/about", label: "About" },
              { href: "/work", label: "Work" },
              { href: "/contact", label: "Start a project" },
              { href: `mailto:${site.email}`, label: site.email },
            ]}
          />
        </div>

        <div className="mt-12 flex flex-col gap-2 border-t border-white/5 pt-6 text-xs text-white/40 sm:flex-row sm:items-center sm:justify-between">
          <p>
            © {new Date().getFullYear()} {site.legalName} · {site.locations.join(" · ")}
          </p>
          <p>An India-registered technology company.</p>
        </div>
      </div>
    </footer>
  );
}

function Social({
  href,
  label,
  icon: Icon,
}: {
  href: string;
  label: string;
  icon: typeof Linkedin;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer me"
      aria-label={label}
      className="grid size-9 place-items-center rounded-full border border-white/10 bg-white/[0.03] text-white/60 transition-colors hover:border-white/30 hover:text-white"
    >
      <Icon className="size-4" />
    </a>
  );
}

function FooterCol({
  title,
  links,
}: {
  title: string;
  links: { href: string; label: string }[];
}) {
  return (
    <div>
      <div className="text-xs font-semibold uppercase tracking-[0.14em] text-white/40">
        {title}
      </div>
      <ul className="mt-4 space-y-2.5 text-sm">
        {links.map((l) => (
          <li key={l.href + l.label}>
            <SmartLink
              href={l.href}
              className="text-white/65 transition-colors hover:text-white"
            >
              {l.label}
            </SmartLink>
          </li>
        ))}
      </ul>
    </div>
  );
}
